import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import SectionEditModal from '../profile/SectionEditModal';
import {
  FEEDBACK_PLATFORMS,
  FEEDBACK_POST_TYPES,
} from '../../lib/feedbackPosts/types';
import { feedbackPlatformLabel, feedbackTypeLabel } from '../../lib/feedbackPosts/labels';
import { validateNewFeedbackPost } from '../../lib/feedbackPosts/validation';
import { createFeedbackPost } from '../../lib/feedbackPosts/firestore';

const TYPE_OPTIONS = FEEDBACK_POST_TYPES.map((v) => ({
  value: v,
  label: feedbackTypeLabel(v),
}));

const PLATFORM_OPTIONS = FEEDBACK_PLATFORMS.map((v) => ({
  value: v,
  label: feedbackPlatformLabel(v),
}));

export default function AskFeedbackModal({ isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState('general');
  const [platform, setPlatform] = useState('tiktok');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [feedbackPrompt, setFeedbackPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFeedbackType('general');
      setPlatform('tiktok');
      setTitle('');
      setDescription('');
      setContentUrl('');
      setFeedbackPrompt('');
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!user) {
      setError('Sign in to post.');
      return;
    }
    if (submitting) return;

    const v = validateNewFeedbackPost({
      title,
      description,
      feedbackType,
      platform,
      contentUrl,
      feedbackPrompt,
    });
    if (!v.ok) {
      setError(v.message);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const postId = await createFeedbackPost({
        authorId: user.uid,
        feedbackType,
        platform,
        title: title.trim(),
        description: description.trim(),
        contentUrl: contentUrl.trim() || null,
        feedbackPrompt: feedbackPrompt.trim() || null,
      });
      onSuccess?.(postId);
    } catch (err) {
      setError(err?.message || 'Could not create post. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Ask for feedback"
      subtitle="Specific questions get stronger answers. Say what you are optimizing for (hooks, retention, clarity) and, if you can, link the piece you mean."
      onSave={handleSave}
      isSaving={submitting}
      errors={error ? { _general: error } : {}}
      saveLabel="Post"
    >
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 mb-4">
        <p className="font-medium text-slate-800">Tips</p>
        <ul className="mt-2 list-disc list-inside space-y-1">
          <li>Ask a specific question so creators can give better feedback.</li>
          <li>
            Example: Which opening hook is stronger for a skincare brand
            audience?
          </li>
        </ul>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="ask-fb-type" className="block text-xs font-medium text-slate-600">
            Feedback type <span className="text-red-500">*</span>
          </label>
          <select
            id="ask-fb-type"
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="ask-fb-platform" className="block text-xs font-medium text-slate-600">
            Platform <span className="text-red-500">*</span>
          </label>
          <select
            id="ask-fb-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          >
            {PLATFORM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="ask-fb-title" className="block text-xs font-medium text-slate-600">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="ask-fb-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Hook review for 30s GRWM"
            maxLength={200}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          />
        </div>

        <div>
          <label htmlFor="ask-fb-desc" className="block text-xs font-medium text-slate-600">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="ask-fb-desc"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Context, goal, and what you have already tried."
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-y"
          />
        </div>

        <div>
          <label htmlFor="ask-fb-url" className="block text-xs font-medium text-slate-600">
            Content URL (optional)
          </label>
          <input
            id="ask-fb-url"
            type="url"
            value={contentUrl}
            onChange={(e) => setContentUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400"
          />
        </div>

        <div>
          <label htmlFor="ask-fb-prompt" className="block text-xs font-medium text-slate-600">
            What kind of feedback do you want? (optional)
          </label>
          <textarea
            id="ask-fb-prompt"
            rows={2}
            value={feedbackPrompt}
            onChange={(e) => setFeedbackPrompt(e.target.value)}
            placeholder="e.g. Be blunt on the first 3 seconds only."
            maxLength={500}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-y"
          />
        </div>
      </div>
    </SectionEditModal>
  );
}
